import moment from 'moment'
import React, { FC, useEffect, useRef, useState } from 'react'
import { createUseStyles } from 'react-jss'
import { Delete, DeleteFill, Edit, EditFill } from '../../../assets/icons'
import { Note } from '../../../models/Note'
import { colors } from '../../../theme/palette'
import { L, L_HTML } from '../../providers/Lokalise'
import { Button } from '../../ui/Button/Button'
import { CircleIcon2 } from '../../ui/Circle/Circle'
import { Typography } from '../../ui/Typography/Typography'

interface NoteSidebarCardProps {
    note: Note
    onClickEdit?: (note: Note) => void
    onClickDelete?: (note: Note) => void
}

const useStyles = createUseStyles({
    card: {
        position: 'relative',
        backgroundColor: colors.white,
        width: '100%',
        color: 'black',
        marginBottom: '10px',
        borderRadius: '4px',
        border: `1px solid ${colors.borderColor}`,
        padding: '1rem',
        transition: 'all 0.5s linear',
    },
    actions: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        display: 'flex',
        gap: '10px',
    },
    date: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '20px',
    },
    deleteButtons: {
        display: 'flex',
        transition: 'all .3s ease-in',
        marginTop: '20px',
    },
    title: {
        display: '-webkit-box',
        WebkitLineClamp: '1',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    titleOpen: {
        display: '-webkit-box',
        // WebkitLineClamp: '1',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        // textOverflow: 'ellipsis',
    },
    content: {
        marginTop: '10px',
        display: '-webkit-box',
        WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        whiteSpace: 'pre-wrap',
        textOverflow: 'ellipsis',
    },
    contentOpen: {
        marginTop: '10px',
        display: '-webkit-box',
        whiteSpace: 'pre-wrap',
        // WebkitLineClamp: '2',
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        // textOverflow: 'ellipsis',
    },
})

const NoteSidebarCard: FC<NoteSidebarCardProps> = ({
    note,
    onClickEdit,
    onClickDelete,
}) => {
    const classes = useStyles()
    const [showDelete, setShowDelete] = useState(false)
    const [readMore, setReadMore] = useState(false)
    const [isMore, setIsMore] = useState(false)
    const contentRef = useRef<HTMLSpanElement>()
    const titleRef = useRef<HTMLSpanElement>()

    const onHandleEdit = (): void => {
        if (onClickEdit) {
            onClickEdit(note)
        }
    }

    const onHandleDelete = (): void => {
        setShowDelete(false)
        if (onClickDelete) {
            onClickDelete(note)
        }
    }

    const checkLines = (ref, lines: number): void => {
        // eslint-disable-next-line no-unused-expressions
        const lineHeight = window
            .getComputedStyle(ref.current)
            .getPropertyValue('line-height')

        // eslint-disable-next-line no-unused-expressions
        ref.current &&
            Math.round(ref.current.scrollHeight / parseFloat(lineHeight)) >
                lines &&
            setIsMore(true)
    }
    useEffect(() => {
        checkLines(contentRef, 2)
    }, [contentRef, note])
    useEffect(() => {
        checkLines(titleRef, 1)
    }, [titleRef, note])
    return (
        <div className={classes.card}>
            {/* --------- headline -------- */}
            <Typography
                size="S"
                weight="bold"
                style={{
                    paddingBottom: '.5rem',
                    paddingRight: '100px',
                    wordBreak: 'break-word',
                }}
            >
                <span
                    ref={titleRef}
                    className={readMore ? classes.titleOpen : classes.title}
                >
                    {note.title}
                </span>
            </Typography>
            {/* ------- text content --------- */}
            <Typography size="XS">
                <span
                    ref={contentRef}
                    className={readMore ? classes.contentOpen : classes.content}
                >
                    {note.content}
                </span>
            </Typography>
            {/* ---------- date -------- */}
            <div className={classes.date}>
                <Typography size="XXS" color={colors.labelColor}>
                    {`${moment(note.updated).format('DD.MM.YYYY')}, kl.${moment(
                        note.updated
                    ).format('HH:mm')}`}
                </Typography>
                {isMore && (
                    <Button
                        type="action"
                        style={{
                            textDecoration: 'underline',
                        }}
                        onClick={() => setReadMore((prev) => !prev)}
                    >
                        {readMore
                            ? L_HTML('NOTER_SIDEBAR_VIS_MINDRE')
                            : L_HTML('NOTER_SIDEBAR_VIS_MERE')}
                    </Button>
                )}
            </div>

            <div className={classes.actions}>
                <Button type="action" onClick={() => setShowDelete(true)}>
                    <CircleIcon2 width="34px" backgroundColor={colors.red100}>
                        <DeleteFill width="20px" color={colors.red600} />
                    </CircleIcon2>
                </Button>
                <Button type="action" onClick={onHandleEdit}>
                    <CircleIcon2 width="34px">
                        <EditFill width="22px" color={colors.green500} />
                    </CircleIcon2>
                </Button>
            </div>
            {showDelete ? (
                <div className={`${classes.deleteButtons} page-up`}>
                    <Button
                        onClick={() => setShowDelete(false)}
                        size="xlarge"
                        type="secondary"
                        style={{ flexGrow: 1 }}
                    >
                        {L('NOTER_REDIGER_FORM_ANNULLER_KNAP')}
                    </Button>
                    <Button
                        onClick={onHandleDelete}
                        type="danger"
                        size="xlarge"
                        style={{ flexGrow: 1 }}
                    >
                        {L('NOTER_SIDEBAR_SLET-NOTE_KNAP')}
                    </Button>
                </div>
            ) : null}
        </div>
    )
}

export { NoteSidebarCard }
